import React, { useState } from 'react';
import { Table, Input, Select, Tag, Typography, Space, DatePicker } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { ColumnsType } from 'antd/es/table';
import { API } from './services/api';
import { useIsMobile } from './hooks/useIsMobile';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

interface Article {
  _id: string;
  title: string;
  link: string;
  summary: string;
  author: string;
  publishedAt: string;
  source: string;
  imageUrl: string;
  crawledAt: string;
}

const SOURCE_OPTIONS = [
  { label: '전체', value: '' },
  { label: '네이버', value: 'naver-economy' },
  { label: '다음', value: 'daum-economy' },
  { label: '한국경제', value: 'hankyung-economy' },
  { label: '매경 경제', value: 'maeil-economy' },
  { label: '매경 증권', value: 'maeil-stock' },
  { label: '뉴스핌 경제', value: 'newspim-economy' },
  { label: '뉴스핌 금융', value: 'newspim-finance' },
];

const SOURCE_LABEL: Record<string, string> = {
  'naver-economy': '네이버',
  'daum-economy': '다음',
  'hankyung-economy': '한국경제',
  'maeil-economy': '매경',
  'maeil-stock': '매경증권',
  'newspim-economy': '뉴스핌경제',
  'newspim-finance': '뉴스핌금융',
};

const fetchArticles = async (params: {
  keyword: string;
  source: string;
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
}) => {
  const query = new URLSearchParams();
  if (params.keyword) query.set('keyword', params.keyword);
  if (params.source) query.set('source', params.source);
  if (params.startDate) query.set('startDate', params.startDate);
  if (params.endDate) query.set('endDate', params.endDate);
  query.set('page', String(params.page));
  query.set('limit', String(params.limit));

  const res = await API.get(`/articles/search?${query.toString()}`);
  return res.data as { total: number; contents: Article[] };
};

const ExclusiveNews: React.FC = () => {
  const isMobile = useIsMobile();
  const [keyword, setKeyword] = useState('');
  const [source, setSource] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const startDate = selectedDate?.startOf('day').toISOString() ?? '';
  const endDate = selectedDate?.endOf('day').toISOString() ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['exclusiveNews', { keyword, source, startDate, endDate, page, limit }],
    queryFn: () => fetchArticles({ keyword, source, startDate, endDate, page, limit }),
    refetchOnWindowFocus: false,
  });

  const DOW = ['일', '월', '화', '수', '목', '금', '토'];

  const desktopDateColumn: ColumnsType<Article>[0] = {
    title: '날짜',
    dataIndex: 'crawledAt',
    key: 'crawledAt',
    width: 72,
    render: (val) => {
      const d = dayjs(val);
      if (!d.isValid()) return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
      const dow = DOW[d.day()];
      const isWeekend = d.day() === 0 || d.day() === 6;
      return (
        <div style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            {d.format('MM/DD')}
            <span style={{ color: isWeekend ? '#f5222d' : '#595959', fontSize: 11, marginLeft: 2 }}>
              ({dow})
            </span>
          </div>
        </div>
      );
    },
  };

  const articleColumn: ColumnsType<Article>[0] = {
    title: '기사',
    dataIndex: 'title',
    key: 'title',
    render: (text, record) => {
      const d = dayjs(record.crawledAt);
      const dow = d.isValid() ? DOW[d.day()] : '';
      const isWeekend = d.day() === 0 || d.day() === 6;

      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {record.imageUrl && !isMobile && (
            <img
              src={record.imageUrl}
              alt=""
              style={{ width: 72, height: 50, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            {isMobile && d.isValid() && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                <span style={{ fontWeight: 600, color: isWeekend ? '#f5222d' : '#595959' }}>
                  {d.format('MM/DD')}({dow})
                </span>
              </Text>
            )}
            <a
              href={record.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: isMobile ? 13 : 14, wordBreak: 'keep-all' }}
            >
              {text}
            </a>
            {record.summary && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.summary.slice(0, isMobile ? 60 : 100)}
                {record.summary.length > (isMobile ? 60 : 100) ? '...' : ''}
              </Text>
            )}
            <Space size={4} wrap>
              <Tag color="blue" style={{ fontSize: 10, padding: '0 4px' }}>
                {SOURCE_LABEL[record.source] ?? record.source}
              </Tag>
              {record.author && !isMobile && (
                <Text type="secondary" style={{ fontSize: 11 }}>✍️ {record.author}</Text>
              )}
            </Space>
          </div>
        </div>
      );
    },
  };

  const columns: ColumnsType<Article> = isMobile
    ? [articleColumn]
    : [desktopDateColumn, articleColumn];

  return (
    <div>
      {isMobile ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <Input.Search
            placeholder="제목/본문 검색"
            allowClear
            style={{ gridColumn: '1 / -1' }}
            onSearch={(val) => { setKeyword(val); setPage(1); }}
          />
          <Select
            options={SOURCE_OPTIONS}
            value={source}
            onChange={(val) => { setSource(val); setPage(1); }}
            style={{ width: '100%' }}
            placeholder="매체 선택"
          />
          <DatePicker
            value={selectedDate}
            onChange={(date) => { setSelectedDate(date); setPage(1); }}
            placeholder="날짜 선택"
            style={{ width: '100%' }}
            allowClear
          />
        </div>
      ) : (
        <Space style={{ marginBottom: 12, flexWrap: 'wrap' }} size={8}>
          <Input.Search
            placeholder="제목/본문 검색"
            allowClear
            style={{ width: 260 }}
            onSearch={(val) => { setKeyword(val); setPage(1); }}
          />
          <Select
            options={SOURCE_OPTIONS}
            value={source}
            onChange={(val) => { setSource(val); setPage(1); }}
            style={{ width: 130 }}
          />
          <DatePicker
            value={selectedDate}
            onChange={(date) => { setSelectedDate(date); setPage(1); }}
            placeholder="날짜 선택"
            style={{ width: 140 }}
            allowClear
          />
        </Space>
      )}

      <Table
        columns={columns}
        dataSource={data?.contents ?? []}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total ?? 0,
          onChange: (p) => setPage(p),
          showTotal: (total) => `총 ${total}개`,
          size: isMobile ? 'small' : 'default',
        }}
        size="small"
      />
    </div>
  );
};

export default ExclusiveNews;
